import React, { useState, useEffect } from 'react';
import './App.css';
import { findDateIndex, getPriceDataForDate, daysBetween } from './utils/dateUtils';
import NetWorthChart from './components/NetWorthChart';
import { initPostHog, trackEvent, trackPageView } from './utils/posthog';

function App() {
  // 股票選擇 (目前固定為 0050)
  const [selectedStock, setSelectedStock] = useState('0050');
  const [stockData, setStockData] = useState([]);

  // 使用者輸入
  const [startDate, setStartDate] = useState('2020-01-02');
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [endDate, setEndDate] = useState(getTodayDate());
  const [initialCapital, setInitialCapital] = useState(1000000);
  const [maxDrawdown, setMaxDrawdown] = useState(25);
  const [profitTarget, setProfitTarget] = useState(20); // 假設預設停利20%
  const [capitalAllocations, setCapitalAllocations] = useState([6, 12, 18, 24, 40]); // 新的狀態，陣列

  // 回測結果
  const [backtestResult, setBacktestResult] = useState(null);
  const [error, setError] = useState('');

  // 初始化 PostHog
  useEffect(() => {
    initPostHog();
    trackPageView('股票交易回測');
  }, []);

  // 載入股票資料
  useEffect(() => {
    const fetchStockData = async () => {
      try {
        // 注意：在實際部署時，您可能需要將 CSV 檔案放在 public 資料夾下，
        // 或者設定一個後端 API 來提供資料。
        // 為了簡化原型，我們假設 CSV 檔案與 public/index.html 在同一層級的 stock_data 資料夾
        const response = await fetch(`/stock_data/${selectedStock}.csv`);
        if (!response.ok) {
          throw new Error(`無法載入股票資料: ${response.statusText}`);
        }
        const csvText = await response.text();
        
        const lines = csvText.trim().split('\n');
        if (lines.length < 2) {
          throw new Error("CSV 檔案內容不足，至少需要標頭和一行資料。");
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase()); // 轉換為小寫以方便比對
        const dateIndex = headers.indexOf('date');
        // const adjCloseIndex = headers.indexOf('adj_close'); // 改為讀取 'close'
        const closePriceIndex = headers.indexOf('close');


        if (dateIndex === -1 || closePriceIndex === -1) {
          // throw new Error("CSV 檔案缺少 'date' 或 'adj_close' 欄位。");
          throw new Error(`CSV 檔案缺少 'date' (索引: ${dateIndex}) 或 'close' (索引: ${closePriceIndex}) 欄位。已解析標頭: [${headers.join(', ')}]`);
        }

        const parsedData = lines.slice(1).map((line, i) => {
          const values = line.split(',').map(v => v.trim());
          const dateStr = values[dateIndex];
          // const adjCloseStr = values[adjCloseIndex];
          const closePriceStr = values[closePriceIndex];


          if (!dateStr || !closePriceStr) { // 更新檢查
            console.warn(`第 ${i + 2} 行資料不完整 (日期或收盤價缺失)，跳過此行: ${line}`);
            return null; // 跳過不完整的行
          }

          const priceToUse = parseFloat(closePriceStr); // 使用 closePriceStr
          if (isNaN(priceToUse)) {
            console.warn(`第 ${i + 2} 行 'close' (${closePriceStr}) 不是有效數字，跳過此行: ${line}`);
            return null;
          }
          
          // 簡單的日期格式驗證 (YYYY-MM-DD)
          if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            console.warn(`第 ${i + 2} 行日期格式 (${dateStr}) 不正確，跳過此行: ${line}`);
            return null;
          }

          return {
            date: dateStr, // 保留字串格式，方便後續比對
            adj_close: priceToUse, // 內部仍然使用 adj_close 作為鍵名，但值來自 CSV 的 'close'
          };
        }).filter(entry => entry !== null); // 過濾掉無效的行

        if (parsedData.length === 0) {
            throw new Error("CSV 資料解析後無有效資料。請檢查檔案內容與格式。");
        }

        // 按日期排序，確保資料是時間序列
        parsedData.sort((a, b) => new Date(a.date) - new Date(b.date));

        setStockData(parsedData);
        setError(''); // 清除之前的錯誤
        console.log(`成功載入並解析 ${parsedData.length} 筆 ${selectedStock} 股票資料。`);
      } catch (err) {
        console.error("載入股票資料錯誤:", err);
        setError(`載入股票資料失敗: ${err.message}. 請確認 /public/stock_data/${selectedStock}.csv 檔案存在且格式正確。`);
        setStockData([]); // 清空資料以避免使用舊資料
      }
    };

    if (selectedStock) {
      fetchStockData();
    }
  }, [selectedStock]);

  // 修改 handleBacktest 以接受可選的日期參數
  const performBacktest = (sDate, eDate) => {
    setError('');
    setBacktestResult(null);

    const currentStartDate = sDate || startDate; // 使用傳入的日期或狀態中的日期
    const currentEndDate = eDate || endDate;

    // --- 基本驗證 ---
    if (!currentStartDate || !currentEndDate) {
      setError('請輸入開始日期和結束日期。');
      return;
    }
    if (new Date(currentStartDate) >= new Date(currentEndDate)) {
      setError('開始日期必須早於結束日期。');
      return;
    }
    if (initialCapital <= 0) {
      setError('初始本金必須大於 0。');
      return;
    }
    if (maxDrawdown < 1 || maxDrawdown > 100) {
      setError('預期最大跌幅必須在 1 到 100 之間。');
      return;
    }
    if (profitTarget <= 0) {
      setError('停利 %數必須大於 0。');
      return;
    }

    // const allocations = capitalAllocations.split(',').map(s => parseInt(s.trim(), 10)); // 舊的讀取方式
    const allocations = [...capitalAllocations]; // 直接使用狀態陣列
    if (allocations.some(isNaN) || allocations.length !== 5 || allocations.some(a => a < 0)) {
      // isNaN 檢查現在應該不太會觸發，因為 input type="number"
      // 但保留以防萬一或直接修改狀態導致非數字
      setError('資金分配比例必須是5個非負整數。');
      return;
    }
    const sumAllocations = allocations.reduce((sum, val) => sum + val, 0);
    if (sumAllocations > 100) {
      setError('資金分配比例總和不可超過 100%。');
      return;
    }

    if (stockData.length === 0) {
      // 如果 fetchStockData 已經設定了錯誤訊息，則不再覆蓋
      if (!error) {
        setError('股票資料尚未載入或載入失敗，無法進行回測。請檢查瀏覽器主控台以獲取詳細資訊，並確認 CSV 檔案存在且路徑正確。');
      }
      return;
    }

    // --- 回測邏輯 ---
    // 使用 currentStartDate 和 currentEndDate
    console.log("開始回測:", { selectedStock, startDate: currentStartDate, endDate: currentEndDate, initialCapital, maxDrawdown, profitTarget, allocations });

    // 1. 找到實際的開始日期和結束日期在股票資料中的索引
    const actualStartDateData = getPriceDataForDate(stockData, currentStartDate);
    if (!actualStartDateData) {
      setError(`在股票資料中找不到指定的開始日期 ${currentStartDate} 或之後的有效交易日。`);
      return;
    }
    
    let actualEndIdx = findDateIndex(stockData, currentEndDate, false); // 找不大於 currentEndDate 的最後一個交易日
    if (actualEndIdx === -1 && stockData.length > 0) {
        if (new Date(currentEndDate) < new Date(stockData[0].date)) {
             setError(`指定的結束日期 ${currentEndDate} 早於最早的股票資料日期 ${stockData[0].date}。`);
        } else {
            actualEndIdx = stockData.length - 1;
        }
    } else if (actualEndIdx === -1 && stockData.length === 0) {
        setError(`股票資料為空，無法確定結束日期。`);
        return;
    }
    
    if (actualEndIdx !== -1 && new Date(stockData[actualEndIdx].date) < new Date(actualStartDateData.date)) {
        setError(`調整後的結束日期 ${stockData[actualEndIdx].date} 早於開始日期 ${actualStartDateData.date}。`);
        return;
    }
     if (actualEndIdx === -1) {
        setError(`無法在股票資料中找到有效的結束日期 ${currentEndDate} 或之前的交易日。`);
        return;
    }


    const startIndex = findDateIndex(stockData, actualStartDateData.date, true);

    if (startIndex === -1) {
        setError(`無法定位實際開始日期 ${actualStartDateData.date} 在資料中的索引。`);
        return;
    }
    
    console.log(`實際開始日期: ${actualStartDateData.date}, 價格: ${actualStartDateData.adj_close}`);
    console.log(`實際結束日期 (或之前最近): ${stockData[actualEndIdx].date}, 價格: ${stockData[actualEndIdx].adj_close}`);


    // 2. 初始化帳戶狀態
    let cash = initialCapital;
    let shares = 0;
    // let totalCost = 0; // 總投入成本 - 基準價位是固定的，這個變數可能不需要
    let currentPortfolioValue = initialCapital;
    const transactions = [];
    const dailyNetWorth = [];

    // 3. 計算基準價位 (第一次買入的價格) 和各加碼點
    const basePrice = actualStartDateData.adj_close;
    // 預期最大跌幅除以4，得到4個加碼點位
    const numDrawdownLevels = 4;
    const drawdownPercentageStep = maxDrawdown / numDrawdownLevels; // 每階跌幅百分比
    const drawdownLevels = Array(numDrawdownLevels).fill(0).map((_, i) =>
        basePrice * (1 - (drawdownPercentageStep / 100) * (i + 1))
    );
    let purchasedLevels = Array(numDrawdownLevels).fill(false); // 追蹤4個加碼點是否已買入

    console.log("基準價位:", basePrice);
    console.log("每階跌幅%:", drawdownPercentageStep);
    console.log("加碼點位價格:", drawdownLevels.map(p => p.toFixed(2)));

    // 4. 計算每份資金
    const capitalPerAllocation = allocations.map(percentage => initialCapital * (percentage / 100));
    // let remainingCapitalForAllocations = [...capitalPerAllocation]; // 不需要了，直接用 capitalPerAllocation


    // --- 模擬每日交易 ---
    let stopTrading = false;

    // 初始買入 (第1份資金，對應 allocations[0])
    if (cash >= capitalPerAllocation[0] && capitalPerAllocation[0] > 0) {
        const price = actualStartDateData.adj_close;
        const sharesToBuy = Math.floor(capitalPerAllocation[0] / price);
        if (sharesToBuy > 0) {
            const cost = sharesToBuy * price;
            cash -= cost;
            shares += sharesToBuy;
            transactions.push({ date: actualStartDateData.date, type: '初始買入', price: price, shares: sharesToBuy, cost: Math.round(cost) });
            // purchasedLevels[0] = true; // 第一份資金不是透過跌幅買的，不需要標記 purchasedLevels
            console.log(`初始買入: ${sharesToBuy} 股 @ ${price}, 花費: ${Math.round(cost)}, 剩餘現金: ${Math.round(cash)}`);
        }
    }
    currentPortfolioValue = cash + shares * actualStartDateData.adj_close;
    dailyNetWorth.push({ date: actualStartDateData.date, value: Math.round(currentPortfolioValue) });


    // 從開始日期的下一天開始遍歷到結束日期
    for (let i = startIndex + 1; i <= actualEndIdx; i++) {
        const currentDayData = stockData[i];
        if (!currentDayData) continue;

        const currentDate = currentDayData.date;
        const currentPrice = currentDayData.adj_close;

        if (stopTrading) {
            currentPortfolioValue = cash + shares * currentPrice;
            dailyNetWorth.push({ date: currentDate, value: Math.round(currentPortfolioValue) });
            continue;
        }

        // 檢查加碼點 (4個加碼點，對應 allocations[1] 到 allocations[4])
        // drawdownLevels[j] (j from 0 to 3) 對應第 (j+1) 個跌幅等級
        // capitalPerAllocation[k] (k from 1 to 4) 對應第 (k+1) 份資金
        for (let j = 0; j < numDrawdownLevels; j++) { // 循環 drawdownLevels.length 次 (即4次)
          const capitalAllocationIndex = j + 1; // 第 j 個跌幅等級 (0-3)，使用第 (j+1) 份資金 (allocations[1] 到 allocations[4])
          
          if (capitalAllocationIndex < allocations.length && // 確保 allocations[j+1] (即 allocations[1] 至 allocations[4]) 存在
              currentPrice <= drawdownLevels[j] &&
              !purchasedLevels[j] &&
              capitalPerAllocation[capitalAllocationIndex] > 0 &&
              cash >= capitalPerAllocation[capitalAllocationIndex]) {
              
              const capitalToUse = capitalPerAllocation[capitalAllocationIndex];
              const sharesToBuy = Math.floor(capitalToUse / currentPrice);
    
              if (sharesToBuy > 0) {
                  const cost = sharesToBuy * currentPrice;
                  cash -= cost;
                  shares += sharesToBuy;
                  // 更新交易紀錄中的跌幅百分比顯示
                  transactions.push({ date: currentDate, type: `加碼買入 (跌 ${(drawdownPercentageStep * (j + 1)).toFixed(2)}%)`, price: currentPrice, shares: sharesToBuy, cost: Math.round(cost) });
                  purchasedLevels[j] = true;
                  console.log(`加碼買入 (跌幅等級 ${j+1} @ ${(drawdownPercentageStep * (j + 1)).toFixed(2)}%): ${sharesToBuy} 股 @ ${currentPrice.toFixed(2)}, 花費: ${Math.round(cost)}, 剩餘現金: ${Math.round(cash)}`);
              }
          }
        }

        currentPortfolioValue = cash + shares * currentPrice;
        dailyNetWorth.push({ date: currentDate, value: Math.round(currentPortfolioValue) });

        const currentProfitPercentage = initialCapital > 0 ? (currentPortfolioValue - initialCapital) / initialCapital : 0;
        if (currentProfitPercentage * 100 >= profitTarget) {
            if (shares > 0) {
                const proceeds = shares * currentPrice;
                cash += proceeds;
                transactions.push({ date: currentDate, type: '停利平倉', price: currentPrice, shares: shares, proceeds: Math.round(proceeds) });
                console.log(`停利平倉: ${shares} 股 @ ${currentPrice}, 獲得現金: ${Math.round(proceeds)}, 總現金: ${Math.round(cash)}`);
                shares = 0;
            }
            stopTrading = true;
            currentPortfolioValue = cash;
            const lastNetWorthEntry = dailyNetWorth[dailyNetWorth.length -1];
            if(lastNetWorthEntry.date === currentDate) {
                lastNetWorthEntry.value = Math.round(currentPortfolioValue);
            } else {
                 dailyNetWorth.push({ date: currentDate, value: Math.round(currentPortfolioValue) });
            }
            console.log(`達到停利目標 ${profitTarget}%，停止交易。目前盈利: ${(currentProfitPercentage * 100).toFixed(2)}%`);
        }
    }

    // --- 計算最終結果 ---
    const finalPortfolioValue = dailyNetWorth.length > 0 ? dailyNetWorth[dailyNetWorth.length - 1].value : initialCapital;
    const totalProfitLoss = finalPortfolioValue - initialCapital;
    const periodReturn = initialCapital > 0 ? totalProfitLoss / initialCapital : 0;

    // 計算每筆買入交易的盈虧
    const finalPrice = stockData[actualEndIdx].adj_close; // 結束日期的股價
    
    // 如果回測結束時仍持有股票，新增全部平倉記錄
    if (shares > 0 && !stopTrading) {
      const endDatePrice = stockData[actualEndIdx].adj_close;
      const proceeds = shares * endDatePrice;
      transactions.push({
        date: stockData[actualEndIdx].date,
        type: '全部平倉',
        price: endDatePrice,
        shares: shares,
        proceeds: Math.round(proceeds)
      });
    }

    // 為交易計算盈虧金額
    transactions.forEach(transaction => {
      if (transaction.cost) { 
        // 買入交易：計算該筆買入股票的盈虧
        const profitLossAmount = (finalPrice - transaction.price) * transaction.shares;
        transaction.finalProfitLoss = profitLossAmount;
      } else if (transaction.type === '全部平倉' || transaction.type === '停利平倉') {
        // 平倉時不顯示盈虧，因為盈虧已經在各筆買入交易中顯示
        transaction.finalProfitLoss = undefined;
      }
    });

    let annualizedReturn = 0;
    const firstDateForCalc = dailyNetWorth.length > 0 ? dailyNetWorth[0].date : actualStartDateData.date;
    // 結束日期應該是最後一筆淨值記錄的日期，或者如果沒有交易則是開始日期
    const lastDateForCalc = dailyNetWorth.length > 0 ? dailyNetWorth[dailyNetWorth.length - 1].date : actualStartDateData.date;
    
    const activeTradingDays = daysBetween(firstDateForCalc, lastDateForCalc);
    const totalDaysInPeriod = daysBetween(actualStartDateData.date, stockData[actualEndIdx].date);

    if (activeTradingDays > 0) { // 只有在實際交易天數大於0時才有意義
        if (totalDaysInPeriod >= 365) { // 使用者選擇的總區間是否大於一年
             annualizedReturn = Math.pow(1 + periodReturn, 365 / activeTradingDays) - 1; // 年化應基於實際交易天數
        } else {
            annualizedReturn = 0;
        }
    } else if (activeTradingDays === 0 && periodReturn !== 0) { // 當天買賣停利
        annualizedReturn = 0;
    }
    
    // 篩選回測期間的股價資料
    const backtestPeriodStockPrices = stockData.filter(dayData => {
        const currentDate = new Date(dayData.date);
        const sDate = actualStartDateData && actualStartDateData.date ? new Date(actualStartDateData.date) : null;
        const eDate = lastDateForCalc ? new Date(lastDateForCalc) : null;
        if (!sDate || !eDate) return false;
        return currentDate >= sDate && currentDate <= eDate;
    }).map(dayData => ({ date: dayData.date, value: dayData.adj_close }));

    // 準備交易註解資料
    const transactionAnnotationsMap = new Map();
    transactions.forEach(t => {
      if (!transactionAnnotationsMap.has(t.date)) {
        transactionAnnotationsMap.set(t.date, []);
      }
      // 簡化標籤內容，避免過長
      let simpleType = t.type;
      if (t.type.startsWith('加碼買入')) simpleType = '加碼';
      else if (t.type === '初始買入') simpleType = '買入';
      else if (t.type === '停利平倉') simpleType = '平倉';
      transactionAnnotationsMap.get(t.date).push(simpleType);
    });

    const transactionAnnotations = [];
    transactionAnnotationsMap.forEach((labels, date) => {
      transactionAnnotations.push({
        date: date,
        label: labels.join('/') // 用斜線分隔同一天的多個交易類型
      });
    });

    const result = {
      periodReturn: periodReturn,
      annualizedReturn: annualizedReturn,
      finalCapital: Math.round(finalPortfolioValue),
      totalProfitLoss: Math.round(totalProfitLoss),
      transactions: transactions,
      dailyNetWorth: dailyNetWorth,
      actualStartDate: actualStartDateData.date,
      actualEndDate: lastDateForCalc,
      totalDaysInPeriod: totalDaysInPeriod,
      activeTradingDays: activeTradingDays,
      basePrice: basePrice,
      drawdownLevelsDisplay: drawdownLevels.map(p => p.toFixed(2)),
      backtestPeriodStockPrices: backtestPeriodStockPrices,
      transactionAnnotations: transactionAnnotations, // 加入交易註解資料
    };

    console.log("回測結果:", result);
    
    // 追蹤回測完成事件
    trackEvent('backtest_completed', {
      periodReturn: result.periodReturn,
      annualizedReturn: result.annualizedReturn,
      totalTransactions: result.transactions.length,
      finalCapital: result.finalCapital,
      activeTradingDays: result.activeTradingDays,
      totalDaysInPeriod: result.totalDaysInPeriod
    });
    
    setBacktestResult(result);
  };
  
  const handleBacktest = () => { // 這個函數現在只是簡單地調用 performBacktest，使用狀態中的日期
    trackEvent('backtest_started', {
      selectedStock,
      initialCapital,
      maxDrawdown,
      profitTarget,
      dateRange: `${startDate} to ${endDate}`
    });
    performBacktest(startDate, endDate);
  };

  const handleRandom365DayTest = () => {
    trackEvent('random_365_day_test_clicked');
    
    if (stockData.length === 0) {
      setError('股票資料尚未載入，無法進行隨機日期回測。');
      return;
    }

    const minDate = new Date('2015-01-05');
    const maxPossibleStartDate = new Date('2024-04-01');
    
    // 確保隨機開始日期加上365天後不會超過股票資料的最後日期
    // 或者，如果股票資料不足，則限制隨機範圍
    const lastStockDate = new Date(stockData[stockData.length - 1].date);
    let effectiveMaxStartDate = new Date(Math.min(maxPossibleStartDate.getTime(), new Date(lastStockDate).setDate(lastStockDate.getDate() - 365)));

    if (minDate > effectiveMaxStartDate) {
        setError('可供選擇的隨機日期範圍不足365天。請檢查股票資料範圍或設定。');
        return;
    }

    const randomTime = minDate.getTime() + Math.random() * (effectiveMaxStartDate.getTime() - minDate.getTime());
    const randomStartDate = new Date(randomTime);

    const year = randomStartDate.getFullYear();
    const month = String(randomStartDate.getMonth() + 1).padStart(2, '0');
    const day = String(randomStartDate.getDate()).padStart(2, '0');
    const newStartDate = `${year}-${month}-${day}`;

    const endDateObj = new Date(randomStartDate);
    endDateObj.setDate(endDateObj.getDate() + 365);
    const endYear = endDateObj.getFullYear();
    const endMonth = String(endDateObj.getMonth() + 1).padStart(2, '0');
    const endDay = String(endDateObj.getDate()).padStart(2, '0');
    const newEndDate = `${endYear}-${endMonth}-${endDay}`;

    setStartDate(newStartDate);
    setEndDate(newEndDate);
    
    // 直接調用 performBacktest 並傳入新的日期
    performBacktest(newStartDate, newEndDate);
  };


  return (
    <div className="App">
      <h1>股票交易回測</h1>

      {error && <p style={{color: 'red', fontWeight: 'bold'}}>錯誤: {error}</p>}

      <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: '20px' }}>
        <div style={{ border: '1px solid #ccc', padding: '15px', borderRadius: '5px', width: '45%' }}>
          <h2>參數設定</h2>
          <div>
            <label>
              選擇ETF:
              <select value={selectedStock} onChange={(e) => setSelectedStock(e.target.value)} disabled>
                <option value="0050">0050 (台灣50)</option>
              </select>
            </label>
          </div>
          <div>
            <label>
              開始日期:
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
          </div>
          <div>
            <label>
              結束日期:
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
          </div>
          <div>
            <label>
              初始本金:
              <input type="number" value={initialCapital} onChange={(e) => setInitialCapital(parseFloat(e.target.value))} />
            </label>
          </div>
          <div>
            <label>
              預期自第一次買入後最大跌幅 (%):
              <input type="number" min="1" max="100" value={maxDrawdown} onChange={(e) => setMaxDrawdown(parseInt(e.target.value, 10))} />
            </label>
          </div>
          <div>
            <label>
              停利% (相較於初始本金):
              <input type="number" min="1" value={profitTarget} onChange={(e) => setProfitTarget(parseFloat(e.target.value))} />
            </label>
          </div>
          <div>
            {/* <label>報酬率 % = (總資產/初始本金)-1) x 100%</label> */}
            <label>資金分配比例 (%):</label>
            {capitalAllocations.map((alloc, index) => (
              <div key={index} style={{ display: 'inline-block', marginRight: '10px' }}>
                <label htmlFor={`alloc-${index}`} style={{ marginRight: '5px' }}>
                  第 {index + 1} 份:
                </label>
                <input
                  type="number"
                  id={`alloc-${index}`}
                  min="0" // 允許輸入0，但驗證時會檢查是否為正整數
                  value={alloc}
                  onChange={(e) => {
                    const newAllocations = [...capitalAllocations];
                    const val = parseInt(e.target.value, 10);
                    newAllocations[index] = isNaN(val) ? 0 : val; // 如果輸入無效則設為0或保持原值
                    setCapitalAllocations(newAllocations);
                  }}
                  style={{ width: '50px' }}
                />
              </div>
            ))}
          </div>
          <button onClick={handleBacktest} style={{marginTop: '20px', padding: '10px 15px', marginRight: '10px'}}>開始回測</button>
          <button onClick={handleRandom365DayTest} style={{marginTop: '20px', padding: '10px 15px'}}>隨機365天回測</button>
        </div>

        {backtestResult && (
          <div style={{ border: '1px solid #ccc', padding: '15px', borderRadius: '5px', width: '45%' }}>
            <h2>回測結果</h2>
            <p>回測期間: {backtestResult.actualStartDate} ~ {backtestResult.actualEndDate} ({backtestResult.totalDaysInPeriod} 天)</p>
            {/* <p>實際交易天數: {backtestResult.activeTradingDays} 天</p> */}
            <p>初始本金: {initialCapital.toLocaleString()}</p>
            <p>最終總資產(不含配息): {backtestResult.finalCapital.toLocaleString()}</p>
            <p>總盈虧金額(不含配息): <span style={{color: backtestResult.totalProfitLoss >= 0 ? 'green' : 'red'}}>{backtestResult.totalProfitLoss.toLocaleString()}</span></p>
            <p>期間報酬率: <span style={{color: backtestResult.periodReturn >= 0 ? 'green' : 'red'}}>{(backtestResult.periodReturn * 100).toFixed(2)}%</span></p>
            <p>年化報酬率: {(backtestResult.annualizedReturn * 100).toFixed(2)}% (僅當回測期間 {'>='} 365天且有交易時計算)</p>
            <p>基準價位 (首次買入的價位): {backtestResult.basePrice.toFixed(2)}</p>
            <p>首次買入後，預計加碼價位: {backtestResult.drawdownLevelsDisplay.join(', ')}</p>
            
            <h3>交易紀錄 ({backtestResult.transactions.length} 筆):</h3>
            {backtestResult.transactions.length > 0 ? (
              <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.9em'}}>
                <thead>
                  <tr>
                    <th style={{border: '1px solid #ddd', padding: '4px'}}>日期</th>
                    <th style={{border: '1px solid #ddd', padding: '4px'}}>交易類型</th>
                    <th style={{border: '1px solid #ddd', padding: '4px'}}>價格</th>
                    <th style={{border: '1px solid #ddd', padding: '4px'}}>股數</th>
                    <th style={{border: '1px solid #ddd', padding: '4px'}}>金額</th>
                    <th style={{border: '1px solid #ddd', padding: '4px'}}>累積買入金額</th>
                    <th style={{border: '1px solid #ddd', padding: '4px'}}>金額佔本金比例%</th>
                    <th style={{border: '1px solid #ddd', padding: '4px'}}>累積動用本金比例%</th>
                    <th style={{border: '1px solid #ddd', padding: '4px'}}>盈虧</th>
                  </tr>
                </thead>
                <tbody>
                  {backtestResult.transactions.map((t, index) => {
                    // 計算累積動用資金 (只計算買入交易的成本，不包括停利平倉)
                    const cumulativeCost = backtestResult.transactions
                      .slice(0, index + 1)
                      .filter(transaction => transaction.cost) // 只計算有成本的交易 (買入交易)
                      .reduce((sum, transaction) => sum + transaction.cost, 0);
                    
                    return (
                      <tr key={index}>
                        <td style={{border: '1px solid #ddd', padding: '4px'}}>{t.date}</td>
                        <td style={{border: '1px solid #ddd', padding: '4px'}}>{t.type}</td>
                        <td style={{border: '1px solid #ddd', padding: '4px'}}>{t.price.toFixed(2)}</td>
                        <td style={{border: '1px solid #ddd', padding: '4px'}}>{t.shares}</td>
                        <td style={{border: '1px solid #ddd', padding: '4px'}}>{(t.cost || t.proceeds).toLocaleString()}</td>
                        <td style={{border: '1px solid #ddd', padding: '4px'}}>
                          {(t.type === '停利平倉' || t.type === '全部平倉') ? '-' : cumulativeCost.toLocaleString()}
                        </td>
                        <td style={{border: '1px solid #ddd', padding: '4px'}}>
                          {(t.type === '停利平倉' || t.type === '全部平倉') ? '-' : ((t.cost || t.proceeds) / initialCapital * 100).toFixed(0) + '%'}
                        </td>
                        <td style={{border: '1px solid #ddd', padding: '4px'}}>
                          {t.cost ? (cumulativeCost / initialCapital * 100).toFixed(0) + '%' : '-'}
                        </td>
                        <td style={{border: '1px solid #ddd', padding: '4px'}}>
                          {t.finalProfitLoss !== undefined ? 
                            <span style={{color: t.finalProfitLoss >= 0 ? 'green' : 'red'}}>
                              {t.finalProfitLoss >= 0 ? '+' : ''}{Math.round(t.finalProfitLoss).toLocaleString()}
                            </span> : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : <p>無交易紀錄。</p>}
            
            {backtestResult.dailyNetWorth && backtestResult.dailyNetWorth.length > 0 && (
              <NetWorthChart
                dailyNetWorthData={backtestResult.dailyNetWorth}
                stockPriceData={backtestResult.backtestPeriodStockPrices}
                transactionAnnotations={backtestResult.transactionAnnotations}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;